import { ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import type { GalaxyMeetingRoom } from '@/types/galaxy-data/meeting-room'
import type { GalaxyPackage } from '@/types/galaxy-data/packages'
import type { GalaxyFunctionRoomSetups } from '@/types/galaxy-data/function-room-setups'
import { usePackagesStore } from '@/stores/packages'
import { useCartStore } from '@/stores/cart'
import IconDbDefaultFunctionRoomSetup from '~icons/db/function-roomsetup-default?raw'
import { useGtm } from '@/composables/features/useGtm'
const { event } = useGtm()

interface ConfirmationPopupContent {
  title: string
  body: string
  confirmButton: string
  cancelButton: string
}

interface AbandonmentPopupContent {
  title: string
  body: string
  requestButton: string
  requestUrl: string | undefined
  requestTarget: string | undefined
  contactButton: string
  contactUrl: string | undefined
  contactTarget: string | undefined
}

export const useModalsStore = defineStore('modals', () => {
  const confirmationModalContent = ref<ConfirmationPopupContent | undefined>(undefined)
  const confirmationModalStatus = ref<boolean | undefined>(undefined)
  const abandonmentModalContent = ref<AbandonmentPopupContent | undefined>(undefined)
  const abandonmentModalStatus = ref<boolean | undefined>(undefined)

  function confirm(popupContent: ConfirmationPopupContent) {
    return new Promise((resolve) => {
      confirmationModalStatus.value = undefined
      confirmationModalContent.value = popupContent

      const unWatch = watch(confirmationModalStatus, () => {
        if (confirmationModalStatus.value !== undefined) {
          unWatch()
          confirmationModalContent.value = undefined
          resolve(confirmationModalStatus.value)
        }
      })
    })
  }

  function abandonment(popupContent: AbandonmentPopupContent) {
    return new Promise((resolve) => {
      abandonmentModalStatus.value = undefined
      abandonmentModalContent.value = popupContent

      const unWatch = watch(abandonmentModalStatus, () => {
        if (abandonmentModalStatus.value !== undefined) {
          unWatch()
          abandonmentModalContent.value = undefined
          resolve(abandonmentModalStatus.value)
        }
      })
    })
  }

  const meetingRoomModalContent = ref<
    (GalaxyMeetingRoom & { functionRoomSetups: GalaxyFunctionRoomSetups[] | undefined }) | undefined
  >(undefined)
  function showMeetingRoomModal(
    meetingRoom: GalaxyMeetingRoom,
    functionRoomSetups: GalaxyFunctionRoomSetups[] | undefined = undefined,
  ) {
    meetingRoomModalContent.value = { ...meetingRoom, functionRoomSetups }
  }

  const packageModalContent = ref<GalaxyPackage | undefined>(undefined)
  function showPackageModal(packageItem: GalaxyPackage, event_tracking = true) {
    const packagesStore = usePackagesStore()
    const hotel = window.db2BookingFunnel.getHotel()
    let attendees_bucket = ''
    if (packagesStore.attendees >= 0 && packagesStore.attendees <= 5) attendees_bucket = '0-5'
    if (packagesStore.attendees >= 6 && packagesStore.attendees <= 10) attendees_bucket = '6-10'
    if (packagesStore.attendees >= 11 && packagesStore.attendees <= 20) attendees_bucket = '11-20'
    if (packagesStore.attendees >= 21 && packagesStore.attendees <= 30) attendees_bucket = '21-30'
    if (packagesStore.attendees > 30) attendees_bucket = '+30'

    const affiliation = hotel.name
    const item_brand = hotel.master_item?.name
    const availablePackageItem = packagesStore.availablePackages.find(
      (item) => item.db2_packageid === packageItem.db2_packageid,
    )
    const availablePackageItemIdx = packagesStore.availablePackages.findIndex(
      (item) => item.db2_packageid === packageItem.db2_packageid,
    )

    if (event_tracking) {
      event('select_item', {
        item_list_id: 'meeting_rooms_packages_list',
        item_list_name: 'Meeting Rooms Packages List',
        items: [
          {
            item_id: packageItem.db2_packageid,
            item_name: packageItem.name,
            affiliation: affiliation,
            index: availablePackageItemIdx,
            item_brand: item_brand,
            item_category: 'meeting rooms packages',
            item_list_id: 'meeting_rooms_packages_list',
            item_list_name: 'Meeting Rooms Packages List',
            attendees_bucket: attendees_bucket,
            item_variant: '',
            quantity: 1,
          },
        ],
      })

      event('view_item', {
        currency: availablePackageItem?.displayPrice?.currencyCode,
        items: [
          {
            item_id: packageItem.db2_packageid,
            item_name: packageItem.name,
            affiliation: affiliation,
            index: availablePackageItemIdx,
            item_brand: item_brand,
            item_category: 'meeting rooms packages',
            item_list_id: 'meeting_rooms_packages_list',
            item_list_name: 'Meeting Rooms Packages List',
            item_variant: '',
            attendees_bucket: attendees_bucket,
            quantity: 1,
          },
        ],
      })
    }
    packageModalContent.value = packageItem
  }

  function computeFunctionRoomSetupsFromCurrentPackage() {
    const packagesStore = usePackagesStore()
    const cartStore = useCartStore()
    const { currentPackage } = storeToRefs(cartStore)

    if (currentPackage.value?.galaxyPackage) {
      const FunctionRoomSetups = packagesStore.getDb2FunctionRoomSetups()
      const functionRoomSetupsList: string[] = []
      const functionRoomSetupsValues: GalaxyFunctionRoomSetups[] = []
      currentPackage.value?.apiData.meetingRooms.forEach((item) => {
        item.functionRoomSetups.forEach((roomSetup) => {
          if (!functionRoomSetupsList.includes(roomSetup.valueOf())) {
            functionRoomSetupsList.push(roomSetup.valueOf())
            const defaultFunctionRoomSetups = {
              id: -1,
              cle: roomSetup.valueOf(),
              name: roomSetup.valueOf(),
              default_icon: IconDbDefaultFunctionRoomSetup.toString(),
            }
            const val =
              FunctionRoomSetups.find((item) => item.cle === roomSetup.valueOf()) ||
              defaultFunctionRoomSetups
            functionRoomSetupsValues.push(val)
          }
        })
      })
      currentPackage.value.galaxyPackage.functionRoomSetups = functionRoomSetupsValues
    }
  }

  return {
    confirmationModalContent,
    confirmationModalStatus,
    abandonmentModalContent,
    abandonmentModalStatus,

    meetingRoomModalContent,
    showMeetingRoomModal,

    packageModalContent,
    showPackageModal,

    computeFunctionRoomSetupsFromCurrentPackage,

    confirm,
    abandonment,
  }
})
